import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { SettingsOutlined, TrendingUpOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import useUpdateResourceAllocation from 'redux/slices/containerApp/hooks/useUpdateResourceAllocation';
import AppDelayedRender from '@tenant/core/App/Render/AppDelayedRender';

const ResourceAllocation = ({ 
  data, 
  appInstanceId, 
  containerAppId, 
  refetchDetail = () => {} 
}) => {
  const defaultData = {
    Cpu: 0.5,
    Memory: 1.0,
    MinReplicas: 0,
    MaxReplicas: 5,
    ConcurrentHttpRequests: 50,
    CooldownPeriod: 300,
  };

  const [resources, setResources] = useState(defaultData);
  const updateResourceAllocationMutate = useUpdateResourceAllocation();

  // Initialize state with props data
  useEffect(() => {
    const initialData = {
      Cpu: data?.Cpu || 0.5,
      Memory: data?.Memory || 1.0,
      MinReplicas: data?.MinReplicas || 0,
      MaxReplicas: data?.MaxReplicas || 5,
      ConcurrentHttpRequests: data?.ConcurrentHttpRequests || 50,
      CooldownPeriod: data?.CooldownPeriod || 300,
    };
    setResources(initialData);
  }, [data]);

  // Check if there are changes (following Environment Variables pattern)
  const isChanged = useMemo(() => {
    const originalData = {
      Cpu: data?.Cpu || 0.5,
      Memory: data?.Memory || 1.0,
      MinReplicas: data?.MinReplicas || 0,
      MaxReplicas: data?.MaxReplicas || 5,
      ConcurrentHttpRequests: data?.ConcurrentHttpRequests || 50,
      CooldownPeriod: data?.CooldownPeriod || 300,
    };

    const hasChanges = (
      resources.Cpu !== originalData.Cpu ||
      resources.Memory !== originalData.Memory ||
      resources.MinReplicas !== originalData.MinReplicas ||
      resources.MaxReplicas !== originalData.MaxReplicas ||
      resources.ConcurrentHttpRequests !== originalData.ConcurrentHttpRequests ||
      resources.CooldownPeriod !== originalData.CooldownPeriod
    );

    return hasChanges;
  }, [resources, data]);

  const handleResourceChange = (field, value) => {
    setResources(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const originalData = {
      Cpu: data?.Cpu || 0.5,
      Memory: data?.Memory || 1.0,
      MinReplicas: data?.MinReplicas || 0,
      MaxReplicas: data?.MaxReplicas || 5,
      ConcurrentHttpRequests: data?.ConcurrentHttpRequests || 50,
      CooldownPeriod: data?.CooldownPeriod || 300,
    };

    // Create payload with only changed fields
    const changedFields = {};
    
    if (resources.Cpu !== originalData.Cpu) {
      changedFields.Cpu = resources.Cpu;
    }
    if (resources.Memory !== originalData.Memory) {
      changedFields.Memory = resources.Memory;
    }
    if (resources.MinReplicas !== originalData.MinReplicas) {
      changedFields.MinReplicas = resources.MinReplicas;
    }
    if (resources.MaxReplicas !== originalData.MaxReplicas) {
      changedFields.MaxReplicas = resources.MaxReplicas;
    }
    if (resources.ConcurrentHttpRequests !== originalData.ConcurrentHttpRequests) {
      changedFields.ConcurrentHttpRequests = resources.ConcurrentHttpRequests;
    }
    if (resources.CooldownPeriod !== originalData.CooldownPeriod) {
      changedFields.CooldownPeriod = resources.CooldownPeriod;
    }

    updateResourceAllocationMutate.mutate(
      {
        appInstanceId,
        containerAppId,
        ...changedFields,
      },
      {
        onSuccess: () => {
          refetchDetail();
        },
      },
    );
  };

  const handleReset = () => {
    const originalData = {
      Cpu: data?.Cpu || 0.5,
      Memory: data?.Memory || 1.0,
      MinReplicas: data?.MinReplicas || 0,
      MaxReplicas: data?.MaxReplicas || 5,
      ConcurrentHttpRequests: data?.ConcurrentHttpRequests || 50,
      CooldownPeriod: data?.CooldownPeriod || 300,
    };
    setResources(originalData);
  };

  const cpuOptions = [0.25, 0.5, 1.0, 1.5, 2.0, 4.0];
  const memoryOptions = [0.5, 1.0, 2.0, 4.0, 8.0];

  return (
    <AppDelayedRender>
      <Box sx={{ p: 2 }}>
        {isChanged && (
          <Box mb={3}>
            <Alert
              severity='info'
              action={
                <>
                  <LoadingButton
                    disabled={updateResourceAllocationMutate?.isPending}
                    size='small'
                    color='error'
                    onClick={handleReset}
                  >
                    Reset
                  </LoadingButton>
                  <LoadingButton
                    loading={updateResourceAllocationMutate?.isPending}
                    size='small'
                    onClick={handleSave}
                  >
                    Save Changes
                  </LoadingButton>
                </>
              }
            >
              You have changed resource allocation settings. Do you want to update the changes?
            </Alert>
          </Box>
        )}

      <Grid container spacing={3}>
        {/* Resource Allocation Section */}
        <Grid item xs={12}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 3,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 2,
                }}
              >
                <SettingsOutlined sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Resource Allocation
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="cpu-cores-label">CPU Cores</InputLabel>
                    <Select
                      labelId="cpu-cores-label"
                      id="cpu-cores"
                      value={resources.Cpu}
                      label="CPU Cores"
                      onChange={(e) => handleResourceChange('Cpu', e.target.value)}
                    >
                      {cpuOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="memory-gi-label">Memory (Gi)</InputLabel>
                    <Select
                      labelId="memory-gi-label"
                      id="memory-gi"
                      value={resources.Memory}
                      label="Memory (Gi)"
                      onChange={(e) => handleResourceChange('Memory', e.target.value)}
                    >
                      {memoryOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 3 }}>
                Note: Adjust CPU and memory to balance performance and cost.
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Scale Rules Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 3,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 2,
                }}
              >
                <TrendingUpOutlined sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Scale Rules
                </Typography>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Min Replicas"
                    value={resources.MinReplicas}
                    onChange={(e) => handleResourceChange('MinReplicas', parseInt(e.target.value) || 0)}
                    inputProps={{
                      min: 0,
                      max: 10,
                      step: 1,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Replicas"
                    value={resources.MaxReplicas}
                    onChange={(e) => handleResourceChange('MaxReplicas', parseInt(e.target.value) || 5)}
                    inputProps={{
                      min: resources.MinReplicas,
                      max: 30,
                      step: 1,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Concurrent Requests"
                    value={resources.ConcurrentHttpRequests}
                    onChange={(e) => handleResourceChange('ConcurrentHttpRequests', parseInt(e.target.value) || 50)}
                    inputProps={{
                      min: 1,
                      max: 1000,
                      step: 1,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Cooldown Period (seconds)"
                    value={resources.CooldownPeriod}
                    onChange={(e) => handleResourceChange('CooldownPeriod', parseInt(e.target.value) || 300)}
                    inputProps={{
                      min: 30,
                      max: 3600,
                      step: 30,
                    }}
                  />
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 3 }}>
                <strong>Auto-scaling behavior:</strong> The app scales automatically when concurrent requests exceed the threshold. 
                <br />
                <strong>Optimization tip:</strong> For lightweight workloads (static sites, frontend apps, etc), set concurrent requests to 500+ to maximize cost efficiency. 
                For CPU-intensive tasks, use lower values (10-50) to maintain performance.
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
    </AppDelayedRender>
  );
};

export default ResourceAllocation;

ResourceAllocation.propTypes = {
  data: PropTypes.object,
  appInstanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containerAppId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  refetchDetail: PropTypes.func,
};
